import { useRef, useState } from 'react';
import { useSnackbarStore } from '@certara/common-react-components';
import { validateFiles } from '@/utils/file-validation';

export interface DragDropState {
  isDragging: boolean;
  dragItemIds: string[];
}

export interface ValidationFailure {
  fileName: string;
  path: string;
  error: string;
}

export interface ValidationWarning {
  fileName: string;
  path: string;
  warning: string;
}

export interface UseDragDropOptions {
  onFilesDropped?: (
    files: File[],
    targetPath: string,
    validationFailures?: ValidationFailure[],
    validationWarnings?: ValidationWarning[]
  ) => Promise<void>;
  onItemsDropped?: (itemIds: string[], targetPath: string) => Promise<void>;
  onDragStart?: (itemIds: string[]) => void;
  onDragEnd?: () => void;
  onDragOver?: (isDragging: boolean) => void;
}

export interface DragDropZoneRef {
  dragState: DragDropState;
}

/**
 * Hook for managing drag and drop operations
 * Handles both file uploads and file movements
 *
 * FILE VALIDATION & SANITIZATION:
 * This hook validates all dropped files using the same rules as the Upload dialog:
 *
 * ✅ ALLOWED CHARACTERS (Files & Folders):
 * - Letters: a-z (uppercase auto-converted to lowercase)
 * - Numbers: 0-9
 * - Symbols: - _ . ! ' ( ) * &
 * - Spaces (including leading and trailing spaces)
 * - Files that only have extensions as their name (.pdf, .gitignore, etc.)
 *
 * ❌ REJECTED CHARACTERS (Auto-sanitized with warnings):
 * - Special chars: @ # $ % ^ + = [ ] { } ; , < > : " | ? \ / ~ `
 * - Control characters (0x00-0x1F)
 *
 * ⚠️ AUTO-SANITIZATION (Shows warnings in upload panel):
 * - Uppercase → lowercase
 * - Remove trailing periods
 * - Leading period (hidden files) → warn but allow
 *
 * 📏 PATH LIMITS:
 * - Total path length: 1024 characters (S3 limit)
 * - Paths exceeding limit are rejected with error
 *
 * IMPORTANT: Validation happens per-file during the drop event processing.
 * Files are validated individually via validateFilePath(), and validation
 * failures/warnings are passed to onFilesDropped for display in the upload panel.
 */
export function useDragDrop(options: UseDragDropOptions = {}) {
  const { onFilesDropped, onItemsDropped, onDragStart, onDragEnd, onDragOver } = options;
  const { addSnackbar } = useSnackbarStore();

  const [dragState, setDragState] = useState<DragDropState>({
    isDragging: false,
    dragItemIds: [],
  });

  const dragCounter = useRef(0);

  const handleDragEnter = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    dragCounter.current++;

    if (event.dataTransfer?.items && dragCounter.current === 1) {
      setDragState((prev) => ({
        ...prev,
        isDragging: true,
      }));
      onDragOver?.(true);
    }
  };

  const handleDragLeave = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    dragCounter.current--;

    if (dragCounter.current === 0) {
      setDragState((prev) => ({
        ...prev,
        isDragging: false,
      }));
      onDragOver?.(false);
    }
  };

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  };

  interface FileReadResult {
    files: File[];
    errors: Array<{ path: string; error: string }>;
    warnings: Array<{ path: string; warnings: string[] }>;
  }

  /**
   * Recursively read entries from a directory
   *
   * This function:
   * 1. Reads files/folders from FileSystemEntry objects
   * 2. Validates each file path against the allowed character rules
   * 3. Applies sanitization (lowercase, trim spaces, remove trailing periods)
   * 4. Collects validation errors and warnings for display
   * 5. Sets webkitRelativePath on each file for folder structure preservation
   *
   * Validation follows the same rules as file-validation.ts:
   * - Allowed: a-z 0-9 - _ . ! ' ( ) * & space
   * - Rejected/Sanitized: @ # $ % ^ + = [ ] { } ; , < > : " | ? \ / ~ `
   */
  const readEntryAsync = (entry: FileSystemEntry): Promise<FileReadResult> => {
    return new Promise((resolve) => {
      if (entry.isFile) {
        (entry as FileSystemFileEntry).file(
          (file) => {
            const relativePath = entry.fullPath.startsWith('/')
              ? entry.fullPath.substring(1)
              : entry.fullPath;

            try {
              Object.defineProperty(file, 'webkitRelativePath', {
                value: relativePath,
                writable: false,
                enumerable: true,
                configurable: true,
              });
            } catch (error) {
              console.warn('Failed to set webkitRelativePath:', error);
            }

            resolve({
              files: [file],
              errors: [],
              warnings: [],
            });
          },
          (error) => {
            resolve({
              files: [],
              errors: [
                {
                  path: entry.fullPath,
                  error: error?.toString() || 'Failed to read file',
                },
              ],
              warnings: [],
            });
          }
        );
      } else if (entry.isDirectory) {
        const dirReader = (entry as FileSystemDirectoryEntry).createReader();
        const allFiles: File[] = [];
        const allErrors: Array<{ path: string; error: string }> = [];
        const allWarnings: Array<{ path: string; warnings: string[] }> = [];

        const readBatch = () => {
          dirReader.readEntries(
            async (entries) => {
              if (entries.length === 0) {
                resolve({
                  files: allFiles,
                  errors: allErrors,
                  warnings: allWarnings,
                });
                return;
              }

              const results = await Promise.all(entries.map(readEntryAsync));
              for (const result of results) {
                allFiles.push(...result.files);
                allErrors.push(...result.errors);
                allWarnings.push(...result.warnings);
              }

              readBatch();
            },
            (error) => {
              resolve({
                files: allFiles,
                errors: [
                  ...allErrors,
                  {
                    path: entry.fullPath,
                    error: error?.toString() || 'Failed to read directory',
                  },
                ],
                warnings: allWarnings,
              });
            }
          );
        };

        readBatch();
      } else {
        resolve({
          files: [],
          errors: [],
          warnings: [],
        });
      }
    });
  };

  /**
   * Handle drop event
   *
   * VALIDATION & SANITIZATION FLOW:
   * 1. Extract all FileSystemEntry items from the drop event
   * 2. For each entry (file/folder):
   *    - Read the file contents
   *    - Validate the file path against allowed characters
   *    - Apply sanitization (lowercase, remove special chars, trim spaces)
   *    - Collect any validation errors or warnings
   * 3. Set sanitized paths on File objects (via webkitRelativePath property)
   * 4. Pass all files + validation failures + warnings to onFilesDropped
   * 5. Upload panel will display warnings and block invalid files
   *
   * ALLOWED: a-z 0-9 - _ . ! ' ( ) * & space
   * REJECTED: @ # $ % ^ + = [ ] { } ; , < > : " | ? \ / ~ ` (auto-removed with warnings)
   */
  const handleDrop = async (event: DragEvent, targetPath: string) => {
    event.preventDefault();
    event.stopPropagation();

    dragCounter.current = 0;

    setDragState((prev) => ({
      ...prev,
      isDragging: false,
    }));
    onDragOver?.(false);

    if (event.dataTransfer?.items) {
      const files: File[] = [];
      const itemIds: string[] = [];
      const allErrors: Array<{ path: string; error: string }> = [];
      const allWarnings: Array<{ path: string; warnings: string[] }> = [];

      const syncItems: Array<
        { kind: 'entry'; entry: FileSystemEntry } | { kind: 'fallback'; file: File }
      > = [];
      const stringPromises: Promise<void>[] = [];

      for (let i = 0; i < event.dataTransfer.items.length; i++) {
        const item = event.dataTransfer.items[i];

        if (item.kind === 'file') {
          const entry = item.webkitGetAsEntry?.();
          if (entry) {
            syncItems.push({ kind: 'entry', entry });
          } else {
            const file = item.getAsFile();
            if (file) {
              syncItems.push({ kind: 'fallback', file });
            }
          }
        } else if (item.kind === 'string' && item.type === 'application/x-items') {
          const promise = new Promise<void>((resolve) => {
            item.getAsString((data) => {
              try {
                const ids = JSON.parse(data) as string[];
                itemIds.push(...ids);
              } catch (e) {
                addSnackbar(
                  'Failed to parse dropped item IDs.',
                  'error',
                  e instanceof Error ? e.message : String(e),
                  true
                );
              }
              resolve();
            });
          });
          stringPromises.push(promise);
        }
      }

      // Await all string promises before continuing
      await Promise.all(stringPromises);

      for (const syncItem of syncItems) {
        if (syncItem.kind === 'entry') {
          const result = await readEntryAsync(syncItem.entry);
          files.push(...result.files);
          allErrors.push(...result.errors);
          allWarnings.push(...result.warnings);
        } else if (syncItem.kind === 'fallback') {
          const { file } = syncItem;
          files.push(file);
        }
      }

      // Batch validate all files to detect collisions
      const validationResults = validateFiles(files);
      const validatedFiles: File[] = [];

      for (const file of files) {
        const filePath =
          (file as File & { webkitRelativePath?: string }).webkitRelativePath ||
          file.name;
        const validationResult = validationResults.get(filePath);

        if (!validationResult) {
          continue;
        }

        if (!validationResult.isValid) {
          allErrors.push({
            path: filePath,
            error: validationResult.error || 'Invalid file name',
          });
        } else {
          if (
            validationResult.sanitizedName &&
            validationResult.sanitizedName !== filePath
          ) {
            const sanitizedFile = new File(
              [file],
              validationResult.sanitizedName.split('/').pop() ||
                validationResult.sanitizedName,
              { type: file.type }
            );

            Object.defineProperty(sanitizedFile, '__originalPath', {
              value: filePath,
              writable: false,
              enumerable: false,
              configurable: true,
            });

            Object.defineProperty(sanitizedFile, 'webkitRelativePath', {
              value: validationResult.sanitizedName,
              writable: false,
            });
            validatedFiles.push(sanitizedFile);

            if (validationResult.warnings) {
              allWarnings.push({
                path: validationResult.sanitizedName,
                warnings: validationResult.warnings,
              });
            }
          } else {
            validatedFiles.push(file);

            if (validationResult.warnings) {
              allWarnings.push({
                path: filePath,
                warnings: validationResult.warnings,
              });
            }
          }
        }
      }

      const validationFailures: ValidationFailure[] = allErrors.map((e) => ({
        fileName: e.path,
        path: e.path,
        error: e.error,
      }));

      const validationWarnings: ValidationWarning[] = allWarnings.map((w) => ({
        fileName: w.path,
        path: w.path,
        warning: w.warnings.join('; '),
      }));

      if (validatedFiles.length > 0 && onFilesDropped) {
        await onFilesDropped(
          validatedFiles,
          targetPath,
          validationFailures,
          validationWarnings
        );
      } else if (validatedFiles.length === 0 && validationFailures.length > 0) {
        if (onFilesDropped) {
          await onFilesDropped([], targetPath, validationFailures, validationWarnings);
        }
      }

      if (itemIds.length > 0 && onItemsDropped) {
        await onItemsDropped(itemIds, targetPath);
      }
    }

    onDragEnd?.();
  };

  const attachDropZone = (element: HTMLElement | null, targetPath: string) => {
    if (!element) return;

    const onDragEnterWrapper = handleDragEnter;
    const onDragLeaveWrapper = handleDragLeave;
    const onDragOverWrapper = handleDragOver;
    const onDropWrapper = (e: DragEvent) => handleDrop(e, targetPath);

    element.addEventListener('dragenter', onDragEnterWrapper);
    element.addEventListener('dragleave', onDragLeaveWrapper);
    element.addEventListener('dragover', onDragOverWrapper);
    element.addEventListener('drop', onDropWrapper);

    return () => {
      element.removeEventListener('dragenter', onDragEnterWrapper);
      element.removeEventListener('dragleave', onDragLeaveWrapper);
      element.removeEventListener('dragover', onDragOverWrapper);
      element.removeEventListener('drop', onDropWrapper);
    };
  };

  const startDrag = (itemIds: string[], event: React.DragEvent) => {
    setDragState((prev) => ({
      ...prev,
      dragItemIds: itemIds,
    }));

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('application/x-items', JSON.stringify(itemIds));

    onDragStart?.(itemIds);
  };

  const endDrag = () => {
    setDragState((prev) => ({
      ...prev,
      dragItemIds: [],
    }));
    onDragEnd?.();
  };

  return {
    dragState,
    attachDropZone,
    startDrag,
    endDrag,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  };
}
